import { RADIUS } from "@elyxeer/ui";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

import { track } from "~/lib/analytics";
import { ApiError, bookEvent, cancelEventBooking } from "~/lib/api";
import { addToCalendar } from "~/lib/calendar-export";
import { openExternal } from "~/lib/external-link";
import { queryKeys } from "~/lib/query-client";
import { AnalyticsEventName } from "~/types/analytics";
import type { EventDetail } from "~/types/event";

/**
 * The booking block of an event: what the member can do about this event right now.
 *
 * One component, five states, because the question it answers changes with the clock:
 * before the event it books a place (or shows the one held), while it runs it is the
 * way in, afterwards it says so and, once the editor attaches a recording, it plays it.
 * The state comes from the server with the event, so this never does date arithmetic.
 *
 * Booking is optimistic in the small way that matters — the button disables while the
 * request runs — and pessimistic in the one that does: the detail is refetched after
 * every change, so "Zbývá 3 místa" is the server's count, not a local guess.
 */
export function EventBooking({
  event,
  locale,
  userId,
}: {
  event: EventDetail;
  locale: string;
  userId: string;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const subject = {
    subjectType: "event" as const,
    subjectId: event.id,
    subjectKey: event.slug,
  };

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: queryKeys.event(userId, locale, event.slug),
      }),
      queryClient.invalidateQueries({
        queryKey: queryKeys.myEvents(userId, locale),
      }),
    ]);

  const book = useMutation({
    mutationFn: () => bookEvent(event.slug, locale),
    onSuccess: async () => {
      setErrorKey(null);
      track(AnalyticsEventName.EventRegister, subject);
      await refresh();
    },
    onError: (error) => {
      setErrorKey(
        error instanceof ApiError && error.status === 409
          ? "events.booking.fullError"
          : "events.booking.error",
      );
      void refresh();
    },
  });

  const cancel = useMutation({
    mutationFn: () => cancelEventBooking(event.slug),
    onSuccess: async () => {
      setErrorKey(null);
      await refresh();
    },
    onError: () => setErrorKey("events.booking.error"),
  });

  const busy = book.isPending || cancel.isPending;

  const calendar = () => {
    track(AnalyticsEventName.EventAddToCalendar, subject);
    void addToCalendar({ ...event, url: event.stream_url ?? null });
  };

  if (event.recording) {
    return (
      <Box
        data-testid="event.booking"
        sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
      >
        <Button
          component={RouterLink}
          to={`/news/${event.recording.slug}`}
          variant="contained"
          size="large"
          startIcon={<PlayCircleOutlineIcon />}
          data-testid="event.recording"
          sx={{ alignSelf: "flex-start" }}
        >
          {t("events.booking.recording")}
        </Button>
      </Box>
    );
  }

  if (event.state === "ended") {
    return (
      <Typography
        variant="T2"
        sx={{ color: "text.secondary" }}
        data-testid="event.booking"
      >
        {t("events.booking.endedHint")}
      </Typography>
    );
  }

  if (event.state === "live") {
    return (
      <Box
        data-testid="event.booking"
        sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
      >
        <Typography variant="T2" sx={{ color: "text.secondary" }}>
          {t("events.booking.liveHint")}
        </Typography>
        {event.stream_url ? (
          <Button
            variant="contained"
            size="large"
            endIcon={<OpenInNewIcon sx={{ fontSize: "1rem" }} />}
            onClick={() => {
              track(AnalyticsEventName.EventJoinStream, subject);
              void openExternal(event.stream_url!);
            }}
            data-testid="event.enter-stream"
            sx={{ alignSelf: "flex-start" }}
          >
            {t("events.booking.enter")}
          </Button>
        ) : null}
      </Box>
    );
  }

  // Upcoming or starting soon: the place is the whole story.
  if (!event.registration_enabled) return null;

  const spotsLine =
    event.spots_left !== null && event.spots_left > 0 ? (
      <Typography
        variant="T3"
        sx={{ color: "text.muted" }}
        data-testid="event.spots-left"
      >
        {t("events.booking.spotsLeft", { count: event.spots_left })}
      </Typography>
    ) : null;

  // A pending place is a public booking of the member's own address whose link was
  // never opened; booking from here confirms it, so it reads as "not yet booked".
  if (event.my_registration?.status === "confirmed") {
    return (
      <Paper
        variant="outlined"
        data-testid="event.booking"
        sx={{
          padding: { xs: 2, md: 2.5 },
          borderRadius: `${RADIUS.lg}px`,
          borderColor: "primary.main",
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CheckCircleOutlineIcon sx={{ color: "primary.main" }} />
          <Typography
            variant="H3"
            sx={{ color: "text.primary" }}
            data-testid="event.booked"
          >
            {t("events.booking.booked")}
          </Typography>
        </Box>
        <Typography variant="T3" sx={{ color: "text.secondary" }}>
          {t("events.booking.bookedHint")}
        </Typography>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            startIcon={<EventAvailableOutlinedIcon fontSize="small" />}
            onClick={calendar}
            data-testid="event.add-to-calendar"
          >
            {t("events.addToCalendar")}
          </Button>
          <Button
            variant="text"
            color="inherit"
            disabled={busy}
            onClick={() => cancel.mutate()}
            data-testid="event.cancel-booking"
          >
            {t("events.booking.cancel")}
          </Button>
        </Box>
        {errorKey ? <Alert severity="error">{t(errorKey)}</Alert> : null}
      </Paper>
    );
  }

  return (
    <Box
      data-testid="event.booking"
      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
    >
      {event.registration_open ? (
        <>
          <Button
            variant="contained"
            size="large"
            disabled={busy}
            onClick={() => book.mutate()}
            data-testid="event.book"
            sx={{ alignSelf: "flex-start" }}
          >
            {t("events.booking.book")}
          </Button>
          {spotsLine}
        </>
      ) : (
        <Typography
          variant="T2"
          sx={{ color: "text.secondary" }}
          data-testid="event.booking-closed"
        >
          {t(
            event.spots_left === 0
              ? "events.booking.full"
              : "events.booking.closed",
          )}
        </Typography>
      )}
      {errorKey ? <Alert severity="error">{t(errorKey)}</Alert> : null}
    </Box>
  );
}
